import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { User, Car, ChevronDown, Loader2, Ban, ShieldCheck, MessageSquare, Mail, AlertTriangle, Wrench, PowerOff, Power } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fixUrl = (u) => (u && !/^https?:\/\//.test(u) ? `${BACKEND}${u.startsWith("/") ? "" : "/"}${u}` : u);

export default function MissionControlActions({ booking, tl, token, onSaved }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const email = tl?.customer?.email;
  const vehicleId = tl?.vehicle_card?.id;
  const [cust360, setCust360] = useState(false);
  const [veh360, setVeh360] = useState(false);
  const [compose, setCompose] = useState(null); // {channel}
  const [noteOpen, setNoteOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  const blocked = tl?.customer_card?.blocked;

  const post = async (url, body, ok) => {
    try { await axios.post(url, body, auth); toast.success(ok); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  const patch = async (url, body, ok) => {
    try { await axios.patch(url, body, auth); toast.success(ok); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  const toggleBlock = () => blocked
    ? post(`${API}/dnr/remove`, { email }, "Customer removed from block list.")
    : post(`${API}/dnr`, { email, name: tl?.customer?.name, reason: "Blocked from booking" }, "Customer added to block list.");

  const startRecovery = () => {
    const aid = tl?.original?.agreement_id;
    if (!aid) { toast.error("No agreement to start recovery on."); return; }
    post(`${API}/agreements/${aid}/recovery`, { stage: "flagged", note: "Recovery review started from booking." }, "Recovery review started.");
  };

  const cItem = (label, fn) => <DropdownMenuItem data-testid={`cust-action-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={fn} className="text-xs cursor-pointer">{label}</DropdownMenuItem>;
  const fItem = (label, fn) => <DropdownMenuItem data-testid={`fleet-action-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={fn} className="text-xs cursor-pointer">{label}</DropdownMenuItem>;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* Customer Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger data-testid={`customer-actions-btn-${booking.id}`}
            className="text-xs flex items-center gap-1.5 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors">
            <User size={13} /> Customer Actions <ChevronDown size={12} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#0C0C0C] border-white/15 text-white max-h-[70vh] overflow-y-auto">
            <DropdownMenuLabel className="text-[10px] uppercase text-white/40">Customer</DropdownMenuLabel>
            {cItem("Open Customer Profile", () => setCust360(true))}
            {cItem("View Customer 360°", () => setCust360(true))}
            {cItem("View Rental History", () => setCust360(true))}
            {cItem("View Agreements", () => setCust360(true))}
            {cItem("View Receipts", () => setCust360(true))}
            {cItem("View Invoices", () => setCust360(true))}
            {cItem("View Payment History", () => setCust360(true))}
            {cItem("View Communication Log", () => setCust360(true))}
            <DropdownMenuSeparator className="bg-white/10" />
            {cItem("Add Note", () => setNoteOpen(true))}
            {cItem("Add Credit", () => setCreditOpen(true))}
            {cItem("Add Internal Flag", () => toast.info("Use the Internal Flags section in the timeline below."))}
            {cItem(blocked ? "Unblock Customer" : "Block Customer", toggleBlock)}
            <DropdownMenuSeparator className="bg-white/10" />
            {cItem("Send SMS", () => setCompose({ channel: "sms" }))}
            {cItem("Send Email", () => setCompose({ channel: "email" }))}
            {cItem("Start Recovery Review", startRecovery)}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Fleet Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger data-testid={`fleet-actions-btn-${booking.id}`}
            className="text-xs flex items-center gap-1.5 border border-blue-400/40 text-blue-300 px-3 py-1.5 rounded hover:bg-blue-400 hover:text-black transition-colors">
            <Car size={13} /> Fleet Actions <ChevronDown size={12} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#0C0C0C] border-white/15 text-white max-h-[70vh] overflow-y-auto">
            <DropdownMenuLabel className="text-[10px] uppercase text-white/40">Vehicle</DropdownMenuLabel>
            {fItem("Open Vehicle Profile", () => setVeh360(true))}
            {fItem("View Vehicle 360°", () => setVeh360(true))}
            {fItem("Rental History", () => setVeh360(true))}
            {fItem("Maintenance History", () => setVeh360(true))}
            {fItem("Damage History", () => setVeh360(true))}
            {fItem("Inspection History", () => setVeh360(true))}
            {fItem("View Profitability", () => setVeh360(true))}
            {fItem("View Lifetime Revenue", () => setVeh360(true))}
            {fItem("View Utilization", () => setVeh360(true))}
            <DropdownMenuSeparator className="bg-white/10" />
            {fItem("Mark Out of Service", () => vehicleId ? patch(`${API}/vehicles/${vehicleId}/status`, { status: "out_of_service" }, "Vehicle marked out of service.") : toast.error("Vehicle not matched."))}
            {fItem("Return to Service", () => vehicleId ? patch(`${API}/vehicles/${vehicleId}/status`, { status: "available" }, "Vehicle returned to service.") : toast.error("Vehicle not matched."))}
            <DropdownMenuSeparator className="bg-white/10" />
            {fItem("Swap Vehicle", () => toast.info("Use the 'Swap Car' button in the booking action row."))}
            {fItem("Schedule Maintenance", () => toast.info("Open the Fleet Maintenance tab to schedule a work order."))}
            {fItem("Upload Photos", () => toast.info("Photo upload is managed on the Vehicle Profile (Fleet tab)."))}
            {fItem("Upload Documents", () => toast.info("Document upload is managed on the Vehicle Profile (Fleet tab)."))}
            {fItem("Generate Vehicle Report", () => setVeh360(true))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Customer 360 slide-out */}
      <Sheet open={cust360} onOpenChange={setCust360}>
        <SheetContent side="right" className="bg-[#0A0A0A] border-white/15 text-white w-full sm:max-w-xl overflow-y-auto" data-testid="customer-360-panel">
          <SheetHeader><SheetTitle className="text-white flex items-center gap-2"><User size={18} className="text-[#F2B705]" /> Customer 360°</SheetTitle></SheetHeader>
          <Customer360 email={email} token={token} />
        </SheetContent>
      </Sheet>

      {/* Vehicle 360 slide-out */}
      <Sheet open={veh360} onOpenChange={setVeh360}>
        <SheetContent side="right" className="bg-[#0A0A0A] border-white/15 text-white w-full sm:max-w-xl overflow-y-auto" data-testid="vehicle-360-panel">
          <SheetHeader><SheetTitle className="text-white flex items-center gap-2"><Car size={18} className="text-blue-300" /> Vehicle 360°</SheetTitle></SheetHeader>
          <Vehicle360 vehicleId={vehicleId} token={token} />
        </SheetContent>
      </Sheet>

      {/* Compose message */}
      <Dialog open={!!compose} onOpenChange={(o) => !o && setCompose(null)}>
        <DialogContent className="bg-[#0C0C0C] border-white/15 text-white" data-testid="compose-message-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{compose?.channel === "sms" ? <MessageSquare size={16} /> : <Mail size={16} />} Send {compose?.channel === "sms" ? "SMS" : "Email"}</DialogTitle></DialogHeader>
          <ComposeForm booking={booking} channel={compose?.channel} token={token} onDone={() => { setCompose(null); onSaved?.(); }} />
        </DialogContent>
      </Dialog>

      {/* Add note */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="bg-[#0C0C0C] border-white/15 text-white" data-testid="add-note-dialog">
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <SimpleForm placeholder="Internal note about this customer…" submitLabel="Save Note"
            onSubmit={async (text) => { await post(`${API}/customers/${encodeURIComponent(email)}/notes`, { text }, "Note added."); setNoteOpen(false); }} />
        </DialogContent>
      </Dialog>

      {/* Add credit */}
      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent className="bg-[#0C0C0C] border-white/15 text-white" data-testid="add-credit-dialog">
          <DialogHeader><DialogTitle>Add Credit</DialogTitle></DialogHeader>
          <CreditForm onSubmit={async (b) => { await post(`${API}/customers/${encodeURIComponent(email)}/credits`, b, "Credit added."); setCreditOpen(false); }} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function ComposeForm({ booking, channel, token, onDone }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) { toast.error("Enter a message."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/send-message`, { channel, subject, body }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${channel.toUpperCase()} sent to ${data.to}.`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {channel === "email" && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="compose-subject"
        className="w-full bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm" />}
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Type your message…" data-testid="compose-body"
        className="w-full bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm" />
      <button onClick={send} disabled={busy} data-testid="compose-send" className="w-full bg-[#F2B705] text-black font-semibold text-sm py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="animate-spin" size={14} />} Send {channel === "sms" ? "SMS" : "Email"}
      </button>
    </div>
  );
}

function SimpleForm({ placeholder, submitLabel, onSubmit }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={placeholder} data-testid="simple-form-text"
        className="w-full bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm" />
      <button data-testid="simple-form-submit" disabled={busy || !text.trim()} onClick={async () => { setBusy(true); await onSubmit(text); setBusy(false); }}
        className="w-full bg-[#F2B705] text-black font-semibold text-sm py-2 rounded disabled:opacity-60">{submitLabel}</button>
    </div>
  );
}

function CreditForm({ onSubmit }) {
  const [type, setType] = useState("free_day");
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="credit-type" className="bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm">
          <option value="free_day">Free Day(s)</option><option value="dollar">$ Off</option><option value="percent">% Off</option>
        </select>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credit-amount" className="w-24 bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm" />
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="credit-reason" className="w-full bg-[#0A0A0A] border border-white/15 rounded px-3 py-2 text-sm" />
      <button data-testid="credit-submit" disabled={busy} onClick={async () => { setBusy(true); await onSubmit({ type, amount: parseFloat(amount) || 1, reason }); setBusy(false); }}
        className="w-full bg-[#F2B705] text-black font-semibold text-sm py-2 rounded disabled:opacity-60">Add Credit</button>
    </div>
  );
}

/* ---------------- Customer 360 ---------------- */
function Customer360({ email, token }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    axios.get(`${API}/customers/${encodeURIComponent(email)}/360`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => on && setD(data)).catch(() => toast.error("Could not load Customer 360°."));
    return () => { on = false; };
  }, [email, token]);
  if (d === null) { return <div className="flex items-center gap-2 text-white/50 text-sm mt-6"><Loader2 className="animate-spin" size={14} /> Loading…</div>; }
  const s = d.stats;
  const kpis = [["Total Rentals", s.total_rentals], ["Lifetime Revenue", money(s.lifetime_revenue)], ["Outstanding", money(s.outstanding)],
    ["Late Returns", s.late_returns], ["Damage Claims", s.damage_claims], ["Rating", `${s.rating}/5`]];
  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        {d.documents?.selfie ? <img src={fixUrl(d.documents.selfie)} alt="c" className="h-14 w-14 rounded-full object-cover border border-white/10" onError={(e) => (e.target.style.display = "none")} />
          : <div className="h-14 w-14 rounded-full bg-[#F2B705]/20 text-[#F2B705] flex items-center justify-center font-bold">{(d.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>}
        <div>
          <div className="font-semibold">{d.name}</div>
          <div className="text-xs text-white/50">{d.email}</div>
          {d.blocked && <span className="text-[10px] text-red-400 border border-red-400/50 rounded px-1.5 py-0.5">🚫 Block List</span>}
        </div>
      </div>
      <Grid items={kpis} />
      <Sec title="Documents">
        <div className="flex gap-3 flex-wrap">
          {[["Driver's License", d.documents?.drivers_license], ["Insurance", d.documents?.insurance], ["Selfie", d.documents?.selfie]].map(([k, u]) => (
            <div key={k} className="text-xs">
              <div className="text-white/40 mb-1">{k}</div>
              {u ? <a href={fixUrl(u)} target="_blank" rel="noreferrer" className="text-[#F2B705] underline">View</a> : <span className="text-white/30">Not on file</span>}
            </div>
          ))}
        </div>
      </Sec>
      <ListSec title={`Rental History (${d.rentals.length})`} rows={d.rentals.map((r) => [`${r.vehicle}`, `${r.pickup_date}→${r.return_date}`, money(r.total)])} />
      <ListSec title={`Agreements (${d.agreements.length})`} rows={d.agreements.map((a) => [a.number, a.kind, a.status])} />
      <ListSec title={`Invoices (${d.invoices.length})`} rows={d.invoices.map((i) => [i.number, money(i.total), `bal ${money(i.balance)}`])} />
      <ListSec title={`Receipts (${d.receipts.length})`} rows={d.receipts.map((r) => [r.number, money(r.amount), r.date])} />
      <ListSec title={`Payment History (${d.payments.length})`} rows={d.payments.map((p) => [p.number || "—", p.method, money(p.amount)])} />
      <ListSec title={`Credits (${d.credits.length})`} rows={d.credits.map((c) => [c.label, c.status, c.reason || ""])} />
      <ListSec title={`Notes (${d.notes.length})`} rows={d.notes.map((n) => [n.text, n.author || "", n.date])} />
      <ListSec title={`Communication Log (${d.communications.length})`} rows={d.communications.map((c) => [`${c.channel} · ${c.status}`, c.type, (c.ts || "").slice(0, 10)])} />
    </div>
  );
}

/* ---------------- Vehicle 360 ---------------- */
function Vehicle360({ vehicleId, token }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!vehicleId) { setD(false); return; }
    let on = true;
    axios.get(`${API}/vehicles/${vehicleId}/360`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => on && setD(data)).catch(() => { toast.error("Could not load Vehicle 360°."); on && setD(false); });
    return () => { on = false; };
  }, [vehicleId, token]);
  if (d === null) { return <div className="flex items-center gap-2 text-white/50 text-sm mt-6"><Loader2 className="animate-spin" size={14} /> Loading…</div>; }
  if (d === false) return <div className="text-white/50 text-sm mt-6">Vehicle could not be matched to this booking.</div>;
  const s = d.stats;
  const info = [["VIN", d.vin || "—"], ["Plate", d.license_plate || "—"], ["Odometer", `${(d.odometer || 0).toLocaleString()} mi`],
    ["Purchase Price", money(d.purchase_price)], ["Purchase Date", d.purchase_date || "—"], ["Status", d.status],
    ["Registration", d.registration_expiry || "—"], ["Oil Due In", d.oil_due_in_miles != null ? `${d.oil_due_in_miles.toLocaleString()} mi` : "—"]];
  const kpis = [["Lifetime Revenue", money(s.lifetime_revenue)], ["Total Rentals", s.total_rentals], ["Rented Days", s.rented_days],
    ["Daily Cost", money(s.daily_ownership_cost)], ["Est. Profit", money(s.estimated_profit)], ["Utilization", `${s.utilization_pct}%`]];
  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        {d.photo ? <img src={fixUrl(d.photo)} alt="v" className="h-16 w-24 rounded object-cover border border-white/10" /> : <div className="h-16 w-24 rounded bg-white/5 flex items-center justify-center"><Car size={20} className="text-white/30" /></div>}
        <div className="font-semibold capitalize">{[d.year, d.make, d.model].filter(Boolean).join(" ")}</div>
      </div>
      <Grid items={kpis} />
      <Sec title="Vehicle Info"><Grid items={info} cols={2} /></Sec>
      <ListSec title={`Rental History (${d.rentals.length})`} rows={d.rentals.map((r) => [r.customer, `${r.pickup_date}→${r.return_date}`, money(r.total)])} />
      <ListSec title={`Maintenance History (${d.maintenance.length})`} rows={d.maintenance.map((w) => [w.title || "Work order", w.status, money(w.cost)])} />
      <ListSec title={`Damage History (${d.damage.length})`} rows={d.damage.map((x) => [x.description, money(x.amount), x.date])} />
      <ListSec title={`Inspection History (${d.inspections.length})`} rows={d.inspections.map((i) => [i.type, `${i.mileage || "—"} mi`, `fuel ${i.fuel || "—"}`])} />
    </div>
  );
}

const Grid = ({ items, cols = 3 }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-${cols} gap-2`}>
    {items.map(([k, v], i) => (
      <div key={i} className="bg-[#0C0C0C] border border-white/10 rounded p-2">
        <div className="text-[9px] uppercase tracking-wide text-white/40">{k}</div>
        <div className="text-sm font-semibold text-white/90 capitalize">{v}</div>
      </div>
    ))}
  </div>
);
const Sec = ({ title, children }) => (
  <div><div className="text-xs uppercase tracking-widest text-white/40 mb-2">{title}</div>{children}</div>
);
const ListSec = ({ title, rows }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-white/40 mb-1">{title}</div>
    {rows.length === 0 ? <div className="text-white/30 text-xs">None</div> : (
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs bg-[#0C0C0C] border border-white/5 rounded px-2 py-1.5">
            <span className="text-white/75 truncate flex-1">{r[0]}</span>
            <span className="text-white/45 truncate">{r[1]}</span>
            <span className="text-white/70 text-right">{r[2]}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);
